import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';
import { invoiceApi, juniperQueryApi, type InvoiceQueryParams } from '../api';

// Query keys
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters: InvoiceQueryParams) => [...invoiceKeys.lists(), { filters }] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  statusCounts: () => [...invoiceKeys.all, 'statusCounts'] as const,
};

/**
 * Query hook for fetching invoices list with filtering and pagination
 */
export function useInvoices(params: InvoiceQueryParams = {}) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: async () => {
      const response = await invoiceApi.getInvoices(params);
      const transformedInvoices = invoiceApi.transformInvoices(response.data);

      return {
        data: transformedInvoices,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        limit: response.limit,
        metadata: response.metadata,
        statusCounts: invoiceApi.calculateStatusCounts(transformedInvoices),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - invoices are slow-changing
  });
}

/**
 * Query hook for fetching a single invoice by Juniper ID
 */
export function useInvoice(juniperId: string) {
  return useQuery({
    queryKey: invoiceKeys.detail(juniperId),
    queryFn: async () => {
      const transaction = await invoiceApi.getInvoice(juniperId);
      return invoiceApi.transformInvoice(transaction);
    },
    enabled: !!juniperId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Query hook for fetching invoice status counts (dashboard cards)
 */
export function useInvoiceStatusCounts(params: InvoiceQueryParams = {}) {
  return useQuery({
    queryKey: [...invoiceKeys.statusCounts(), params],
    queryFn: async () => {
      // CRITICAL: Do NOT use uniqueByMatter - show ALL transactions
      // Remove pagination parameters to get ALL transactions for accurate counts
      const { limit, cursor, ...countParams } = params;
      const response = await invoiceApi.getInvoices({
        ...countParams,
        limit: 1000, // Maximum allowed by API to get as many transactions as possible
      });
      const transformedInvoices = invoiceApi.transformInvoices(response.data);
      return invoiceApi.calculateStatusCounts(transformedInvoices);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Mutation for resending email for a single invoice
 */
export function useResendEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ juniperId, tenantId }: { juniperId: string; tenantId: string }) => {
      const requestId = `resend-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post(
        `/juniper/transactions/${juniperId}/resend`,
        {
          juniperId,
          tenantId,
          requestId,
          requestType: 'resend',
          reason: 'Manual resend from juniper transactions page',
        },
        {
          headers: {
            'X-Request-Timeout': '60000',
          },
        }
      );
    },
    onSuccess: (response: any) => {
      if (response.message) {
        showToast.success(response.message);
      } else {
        showToast.success('Email queued successfully - check back in a few moments...');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error || error?.message || 'Failed to queue email. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk sending emails for multiple invoices
 */
export function useBulkSendEmailsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperIds: string[]) => {
      const requestId = `bulk-send-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-send', {
        juniperIds,
        requestId,
        reason: 'Bulk email send from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      const { queued, ineligible } = response.results || {};
      if (queued > 0) {
        showToast.success(`${queued} email(s) queued for sending - check back in a few moments...`);
      }
      if (ineligible && ineligible.length > 0) {
        showToast.info(`${ineligible.length} transaction(s) were not eligible for sending`);
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue bulk emails. Please try again.'
      );
    },
  });
}

/**
 * Mutation for regenerating PDF for a single invoice
 */
export function useRegeneratePdfMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperId: string) => {
      const requestId = `pdf-regen-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post(
        `/juniper/transactions/${juniperId}/regenerate-pdf`,
        {
          requestId,
          requestType: 'regenerate_pdf',
          reason: 'Manual PDF regeneration from juniper transactions page',
        },
        {
          headers: {
            'X-Request-Timeout': '60000',
          },
        }
      );
    },
    onSuccess: (response: any) => {
      if (response.message) {
        showToast.success(response.message);
      } else {
        showToast.success('PDF regeneration queued - check back in a few moments...');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to queue PDF regeneration. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk PDF regeneration for multiple invoices
 */
export function useBulkRegeneratePdfMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperIds: string[]) => {
      const requestId = `bulk-pdf-regen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-regenerate-pdf', {
        juniperIds,
        requestId,
        reason: 'Bulk PDF regeneration from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      const { queued, ineligible } = response.results || {};
      const { totalMessages, estimatedTotalTime } = response.batchInfo || {};

      if (queued > 0) {
        const messageType = totalMessages > 1 ? ` as ${totalMessages} individual messages` : '';
        const timeMessage = estimatedTotalTime ? ` (~${estimatedTotalTime})` : '';
        showToast.success(`${queued} PDF(s) queued for regeneration${messageType}${timeMessage} - check back in a few moments...`);
      }
      if (ineligible && ineligible.length > 0) {
        showToast.info(`${ineligible.length} transaction(s) were not eligible for PDF regeneration`);
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue bulk PDF regeneration. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk refresh from Clio for multiple invoices
 */
export function useBulkRefreshFromClioMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperIds: string[]) => {
      const requestId = `bulk-refresh-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-reprocess', {
        juniperIds,
        requestId,
        reason: 'Bulk refresh from Clio from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      const { queued, ineligible } = response.results || {};
      if (queued > 0) {
        showToast.success(`${queued} transaction(s) queued for refresh from Clio - check back in a few moments...`);
      }
      if (ineligible && ineligible.length > 0) {
        showToast.info(`${ineligible.length} transaction(s) were not eligible for refresh`);
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue bulk refresh from Clio. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk simulating webhook events for multiple transactions by Juniper ID
 */
export function useBulkSimulateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      juniperIds: string[];
      billType: 'operational' | 'trust';
      eventType: 'update' | 'delete';
      reason?: string;
    }) => {
      const requestId = `bulk-simulate-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return juniperQueryApi.bulkSimulateWebhookEvents({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      const { summary } = response;
      if (summary.successful > 0) {
        showToast.success(`${summary.successful} webhook event${summary.successful > 1 ? 's' : ''} simulated successfully${summary.failed > 0 ? ` (${summary.failed} failed)` : ''}`);
      }
      if (summary.failed > 0 && summary.successful === 0) {
        showToast.warning(`${summary.failed} simulation${summary.failed > 1 ? 's' : ''} failed. Check individual results for details.`);
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to simulate webhook events. Please try again.'
      );
    },
  });
}
